import { getOmnibus } from '@services/omnibus';

export const execOnLoad = (initFunction, selector) => {
  if (!initFunction || !selector) return;

  if (document.readyState != 'loading') {
    [].forEach.call(document.querySelectorAll(selector), (el) => {
      initFunction(el);
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      [].forEach.call(document.querySelectorAll(selector), (el) => {
        initFunction(el);
      });
    });
  }
};

/**
 * Formats a price value according to a specified format template.
 *
 * The function converts the given value into various formatted string representations
 * based on placeholders in the `format` string. It supports multiple formats including
 * comma separators, dot separators, and space separators.
 *
 * @param {Object} params - Parameters object.
 * @param {string} [params.format=window?.dAgency?.strings?.moneyFormat] - A string template for the format.
 *        This template can contain placeholders like `{{amount}}`, `{{amount_no_decimals}}`, etc.
 * @param {number} params.value - The price value to format, provided as an integer in cents.
 *
 * @returns {string|undefined} The formatted price string based on the format template. If `value` or `format` is missing, returns `undefined`.
 */
export const formatPrice = ({ format = window?.dAgency?.strings?.moneyFormat, value }) => {
  if ((!value && value != 0) || !format) return;
  const formattedValue = (parseInt(value) / 100).toFixed(2);
  const placeholders = [
    {
      key: 'amount',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals',
      formatter: () => Math.round(formattedValue),
    },
    {
      key: 'amount_with_comma_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_comma_separator',
      formatter: () => `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    },
    {
      key: 'amount_with_apostrophe_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_space_separator',
      formatter: () => `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    },
    {
      key: 'amount_with_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_with_period_and_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
  ];

  return placeholders.reduce(
    (acc, placeholder) =>
      acc.replaceAll(new RegExp(`{{[ ]*${placeholder.key}[ ]*}}`, 'g'), placeholder.formatter(value)),
    format,
  );
};

/**
 * Initializes a number input field with increment and decrement buttons.
 *
 * @param {Object} params - The parameters for initializing the number field.
 * @param {HTMLElement} params.plusBtn - The button element used to increase the value.
 * @param {HTMLElement} params.minusBtn - The button element used to decrease the value.
 * @param {HTMLElement} params.field - The field element where the value is displayed or entered.
 *
 * @throws {Error} Will log an error if `plusBtn`, `minusBtn`, or `field` is missing.
 */
export const initNumberField = ({ plusBtn, minusBtn, field }) => {
  if (!plusBtn || !minusBtn || !field) {
    console.error('Cannot init number field, missing arguments.');
    return;
  }

  const min = field.dataset.min ? parseInt(field.dataset.min, 10) : null;
  const max = field.dataset.max ? parseInt(field.dataset.max, 10) : null;

  const updateButtonStates = () => {
    if (min !== null) {
      if (value <= min) {
        minusBtn.tagName === 'BUTTON' ? (minusBtn.disabled = true) : minusBtn.setAttribute('disabled', '');
      } else {
        minusBtn.tagName === 'BUTTON' ? (minusBtn.disabled = false) : minusBtn.removeAttribute('disabled');
      }
    }
    if (max !== null) {
      if (value >= max) {
        plusBtn.tagName === 'BUTTON' ? (plusBtn.disabled = true) : plusBtn.setAttribute('disabled', '');
      } else {
        plusBtn.tagName === 'BUTTON' ? (plusBtn.disabled = false) : plusBtn.removeAttribute('disabled');
      }
    }
  };

  const updateValue = (newValue) => {
    const clampedValue = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, newValue));
    field.tagName === 'INPUT' ? (field.value = clampedValue) : (field.innerText = clampedValue);
    if (field.tagName === 'INPUT' && value != clampedValue)
      ['input', 'change'].forEach((evt) => field.dispatchEvent(new Event(evt, { bubbles: true })));
    value = clampedValue;
    updateButtonStates();
  };

  let value = parseInt(field.tagName === 'INPUT' ? field.value : field.innerText);
  updateValue(value);

  // Sync internal state when the input value is changed externally (e.g., cart incremental update).
  // Uses a dedicated custom event to avoid triggering the 'change' listener in initLine (which would call changeCart).
  field.addEventListener('cart-qty-sync', () => {
    const externalVal = parseInt(field.tagName === 'INPUT' ? field.value : field.innerText);
    if (!isNaN(externalVal) && externalVal !== value) {
      value = externalVal;
      updateButtonStates();
    }
  });

  plusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value + 1);
  });

  minusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value - 1);
  });
};

/**
 * Retrieves a CSS property value from the given container element.
 *
 * @param {HTMLElement} container - The element to retrieve the CSS property from.
 * @param {string} prop - The name of the CSS property to retrieve.
 *
 * @returns {string} The value of the given CSS property.
 *
 * @throws {Error} Will log an error if `container` or `prop` is missing.
 */
export const getCssProp = (container, prop) => {
  if (!container || !prop) {
    console.error('Cannot get css prop, missing container or prop.');
    return;
  }

  return getComputedStyle(container)[prop];
};

/**
 * Retrieves a CSS variable value from the given container element.
 *
 * @param {HTMLElement} container - The element to retrieve the CSS variable from.
 * @param {string} variable - The name of the CSS variable to retrieve.
 *
 * @returns {string} The value of the given CSS variable.
 *
 * @throws {Error} Will log an error if `container` or `variable` is missing.
 */
export const getCssVariable = (container, variable) => {
  if (!container || !variable) {
    console.error('Cannot get css variable balue, missing container or variable.');
    return;
  }

  return getComputedStyle(container).getPropertyValue(variable);
};

/**
 * Shows or hides the Omnibus block for the currently selected variant.
 *
 * Omnibus is only relevant while the variant is actually on sale, so the block
 * stays hidden unless the variant has a native discount (compare_at_price
 * higher than price).
 *
 * @param {HTMLElement} container - Element containing the `.omnibus__container` block.
 */
export const updateOmnibus = (container) => {
  if (!container) {
    console.error('Cannot update Omnibus, missing container.');
    return;
  }

  const omnibusContainer = container.querySelector('.omnibus__container[data-sku][data-variant-price]');
  const omnibusValue = container.querySelector('.omnibus__container [data-omnibus-value]');

  if (!omnibusContainer || !omnibusValue) return;

  const variantCompareAtPrice = parseInt(omnibusContainer.dataset.variantCompareAtPrice);
  const variantPrice = parseInt(omnibusContainer.dataset.variantPrice);

  getOmnibus({
    sku: omnibusContainer.dataset.sku,
    currency: omnibusContainer.dataset.currency,
    callbacks: {
      afterFetch: (omnibus) => {
        if (omnibus?.price) {
          // Math.round, not parseInt: 17.9 * 100 === 1789.9999999999998,
          // which parseInt would truncate to 17.89.
          const omnibusPrice = Math.round(omnibus.price * 100);

          const hasNativeDiscount = variantCompareAtPrice > variantPrice;

          if (hasNativeDiscount) {
            omnibusValue.innerText = formatPrice({ value: omnibusPrice });
            omnibusContainer.classList.remove('hidden');
          } else {
            omnibusContainer.classList.add('hidden');
          }
        } else {
          omnibusContainer.classList.add('hidden');
        }
      },
      errorCallback: () => {
        omnibusContainer.classList.add('hidden');
      },
    },
  });
};
